<template>
    <div class="border pa-3 rounded">
        <div class="grid grid-cols-3 gap-2">
            <div class="col-span-2">
                <v-select
                    v-model="pvOffer.$data.solarPanelSelected"
                    :label="$t('offerSampleOfferBasePrice.Modules')"
                    :items="solarPanelOptions"
                    variant="outlined"
                    density="compact"
                    :disabled="readMode"
                    ref="selectedSolarPanelField"
                    class="truncate pt-2"
                    hide-details
                >
                    <template v-slot:item="{ item }">
                        <div
                            class="d-flex align-center py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                            @click="changeSolarPanel(item)"
                        >
                            <div
                                class="text-sm"
                                :class="{
                                    'font-weight-bold text-base':
                                        item.value.sales_points > 1,
                                }"
                            >
                                {{ item.title }}
                            </div>
                        </div>
                    </template>
                </v-select>
            </div>

            <div>
                <v-text-field
                    v-if="readMode"
                    v-model.number="offer.panelsNumber"
                    :label="$t('offerSampleOfferBasePrice.Number of modules')"
                    variant="outlined"
                    density="compact"
                    disabled
                    min="7"
                    type="number"
                    class="pt-2"
                    hide-details
                />

                <v-text-field
                    v-else
                    v-model.number="pvOffer.$data.pvSolarPanelsNumber"
                    :label="$t('offerSampleOfferBasePrice.Number of modules')"
                    variant="outlined"
                    density="compact"
                    :error="pvOffer.$data.pvSolarPanelsNumber < 7"
                    min="6"
                    type="number"
                    class="pt-2"
                    hide-details
                />
            </div>
        </div>

        <div
            class="grid grid-cols-2 bg-gray-50 gap-2 pa-2 shadow-lg rounded-md mt-2"
        >
            <v-select
                v-model="pvOffer.$data.inverterSelected"
                :label="$t('offerSampleOfferBasePrice.Inverter')"
                :items="suitableInverterOptions"
                variant="outlined"
                density="compact"
                :disabled="readMode"
                ref="selectedInverterField"
                class="truncate pt-2"
                hide-details
            >
                <template v-slot:item="{ item }">
                    <div
                        class="d-flex align-center py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                        @click="changeInverter(item)"
                    >
                        <div
                            class="text-sm"
                            :class="{
                                'font-weight-bold text-base':
                                    item.value.sales_points > 1,
                            }"
                        >
                            {{ item.title }}
                        </div>
                    </div>
                </template>
            </v-select>

            <v-text-field
                v-if="locale === 'nl'"
                v-model.number="pvOffer.$data.inverterQuantity"
                :label="$t('offerSampleOfferBasePrice.Quantity')"
                variant="outlined"
                density="compact"
                :disabled="readMode"
                min="1"
                type="number"
                class="truncate pt-2"
                hide-details
            />

            <v-text-field
                v-model.number="pvOffer.$data.strings"
                :label="$t('offerSampleOfferBasePrice.Thong')"
                variant="outlined"
                density="compact"
                :disabled="readMode"
                :min="pvOffer.$data.pvSolarPanelsNumber >= 22 ? 2 : 1"
                type="number"
                :error="
                    pvOffer.$data.strings === 1 &&
                    pvOffer.$data.pvSolarPanelsNumber > 18 &&
                    pvOffer.$data.inverterSelected.brand !== 'SolarEdge'
                "
                class="truncate pt-2"
                hide-details
            />

            <v-text-field
                v-model.number="pvOffer.$data.optimizersNumber"
                :label="$t('offerSampleOfferBasePrice.Optimizers')"
                variant="outlined"
                density="compact"
                :disabled="!areOptimizersAvailable || readMode"
                type="number"
                :error="
                    (pvOffer.$data.inverterSelected.brand === 'SolarEdge' &&
                        pvOffer.$data.optimizersNumber === 0) ||
                    pvOffer.$data.optimizersNumber > pvOffer.$data.pvSolarPanelsNumber
                "
                class="truncate pt-2"
                hide-details
            />
        </div>

        <div class="grid grid-cols-1 mt-4">
            <v-select
                v-if="pvOffer.$data.inverterSelected && pvOffer.$data.inverterSelected.is_hybrid"
                v-model="pvOffer.$data.batterySelected"
                :label="$t('offerSampleOfferBasePrice.Battery')"
                :items="batteryOptions"
                variant="outlined"
                density="compact"
                :disabled="readMode"
                ref="selectedBatteryField"
                :clearable="true"
                :placeholder="$t('offerSampleOfferBasePrice.Lack')"
            >
                <template v-slot:item="{ item }">
                    <div
                        class="d-flex align-center py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                        @click="changeBattery(item)"
                    >
                        <div
                            class="text-sm"
                            :class="{
                                'font-weight-bold text-base':
                                    item.value.sales_points > 1,
                            }"
                        >
                            {{ item.title }}
                        </div>
                    </div>
                </template>
            </v-select>
        </div>

        <div>
            <div class="bg-black text-white py-2 text-sm my-3 text-center">
                {{ $t("offerSampleOfferBasePrice.Details of offer") }}
            </div>
            <div class="text-sm">
                <div class="flex justify-between font-black" v-if="godmode">
                    <span>{{ $t("offerSampleOfferBasePrice.Overhead") }}:</span>
                    <span> {{ pvOffer.margin }}</span>
                </div>
                <div class="flex justify-between font-black">
                    <span>{{ $t("offerSampleOfferBasePrice.Power") }}:</span>
                    <span>{{ installationSize }} kWp</span>
                </div>
                <div class="flex justify-between">
                    <span
                        >{{ pvOffer.$data.pvSolarPanelsNumber }} x
                        {{
                            pvOffer.$data.solarPanelSelected.brand +
                            " " +
                            pvOffer.$data.solarPanelSelected.model
                        }}
                        {{ pvOffer.$data.solarPanelSelected.power_output }}Wp</span
                    >
                    <span v-if="godmode">{{
                        $numberHelper.formatMoney(prices.solarPanels)
                    }}</span>
                </div>
                <div
                    class="flex justify-between"
                    :class="{
                        'bg-red-400':
                            pvOffer.$data.inverterSelected.max_kwp * pvOffer.$data.inverterQuantity <
                            installationSize,
                    }"
                >
                    <span
                        >{{ locale === "nl" ? pvOffer.$data.inverterQuantity + " x " : ""
                        }}{{
                            pvOffer.$data.inverterSelected.brand +
                            " " +
                            pvOffer.$data.inverterSelected.model +
                            " (max " +
                            pvOffer.$data.inverterSelected.max_kwp * pvOffer.$data.inverterQuantity +
                            " kW)"
                        }}</span
                    >
                    <span v-if="godmode">{{
                        $numberHelper.formatMoney(prices.inverter)
                    }}</span>
                </div>
                <div v-if="godmode">
                    <div class="flex justify-between" v-if="pvOffer.$data.batterySelected">
                        <span>{{
                            $t("offerSampleOfferBasePrice.Battery")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(
                                pvOffer.$data.batterySelected.buying_price
                            )
                        }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span>{{
                            $t("offerSampleOfferBasePrice.Mounting System")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.mountingMaterial)
                        }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span>{{
                            $t("offerSampleOfferBasePrice.Installation")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(
                                prices.installerCommission
                            )
                        }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span>{{
                            $t(
                                "offerSampleOfferBasePrice.Cables and accessories"
                            )
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(
                                prices.cablesAndAccessories
                            )
                        }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span>{{
                            $t("offerSampleOfferBasePrice.AC box")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.acBoxPrice)
                        }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span>{{
                            $t("offerSampleOfferBasePrice.DC box")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.dcBoxPrice)
                        }}</span>
                    </div>
                    <div class="flex justify-between" v-if="pvOffer.$data.optimizersNumber">
                        <span>{{
                            $t("offerSampleOfferBasePrice.Optimizers")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.optimizers)
                        }}</span>
                    </div>
                    <div class="flex justify-between" v-if="pvOffer.$data.offerBasis.digging">
                        <span>{{
                            $t("offerSampleOfferBasePrice.Piercing")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.digging)
                        }}</span>
                    </div>
                    <div class="flex justify-between" v-if="pvOffer.$data.offerBasis.lift">
                        <span>{{ $t("offerSampleOfferBasePrice.Lift") }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.lift)
                        }}</span>
                    </div>
                    <div
                        class="flex justify-between"
                        v-if="pvOffer.$data.offerBasis.insurance"
                    >
                        <span>{{
                            $t("offerSampleOfferBasePrice.Insurance")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.insurance)
                        }}</span>
                    </div>
                    <div
                        class="flex justify-between"
                        v-if="pvOffer.$data.offerBasis.grounding"
                    >
                        <span>{{
                            $t("offerSampleOfferBasePrice.Grounding")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.grounding)
                        }}</span>
                    </div>
                    <div
                        class="flex justify-between"
                        v-if="installationSize >= 6.5"
                    >
                        <span>{{
                            $t("offerSampleOfferBasePrice.Fire protection")
                        }}</span>
                        <span>{{
                            $numberHelper.formatMoney(prices.fireProtection)
                        }}</span>
                    </div>
                    <div
                        class="flex justify-between"
                        v-if="pvOffer.$data.offerBasis.stuck_tiles"
                    >
                        <span
                            >{{
                                $t("offerSampleOfferBasePrice.Nailed tile")
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(prices.stuckTiles)
                        }}</span>
                    </div>
                    <div
                        class="flex justify-between"
                        v-if="pvOffer.$data.offerBasis.snow_guards"
                    >
                        <span
                            >{{
                                $t(
                                    "offerSampleOfferBasePrice.Dismantling snow guards"
                                )
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(prices.snowGuardRemoval)
                        }}</span>
                    </div>
                    <div
                        class="flex justify-between"
                        v-if="pvOffer.$data.offerBasis.lightning_rod_removal"
                    >
                        <span
                            >{{
                                $t(
                                    "offerSampleOfferBasePrice.Dismantling the lightning rod"
                                )
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(
                                prices.lightningRodRemoval
                            )
                        }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span
                            >{{
                                $t("offerSampleOfferBasePrice.Transport")
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(prices.transport)
                        }}</span>
                    </div>
                    <template v-for="(extraCost, index) in extraCosts" :key="index">
                        <div v-if="extraCost.selected" class="flex justify-between">
                        <span
                        >{{
                                extraCost.name
                            }}:</span
                        >
                            <span>{{
                                    $numberHelper.formatMoney(extraCost.price)
                                }}</span>
                        </div>
                    </template>

                    <div class="flex justify-between">
                        <span
                            >{{
                                $t("offerSampleOfferBasePrice.Recommendation")
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(prices.referral)
                        }}</span>
                    </div>
                </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
                <img
                    v-if="pvOffer.$data.solarPanelSelected && pvOffer.$data.solarPanelSelected.brand"
                    :src="`/img/calc-results/${pvOffer.$data.solarPanelSelected.brand
                        .toLowerCase()
                        .replace(' ', '-')}.jpg`"
                    class="mw-100 px-3 d-block mx-auto"
                />
                <img
                    v-if="pvOffer.$data.solarPanelSelected && pvOffer.$data.solarPanelSelected.brand"
                    :src="`/img/calc-results/${pvOffer.$data.inverterSelected.brand
                        .toLowerCase()
                        .replace(' ', '-')}.jpg`"
                    class="mw-100 px-3 d-block mx-auto"
                />
            </div>

            <div class="wrap">
                <div
                    class="bg-black text-white py-2 text-sm my-3 text-center"
                    @click="
                        pvOffer.$data.forceCommissionVisibility = !pvOffer.$data.forceCommissionVisibility
                    "
                >
                    {{ $t("offerSampleOfferBasePrice.Price") }}
                </div>

                <div v-if="pvOffer.$data.batterySelected">
                    <div class="flex justify-between">
                        <span
                            >{{
                                $t("offerSampleOfferBasePrice.Battery")
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(
                                pvOffer.$data.batterySelected.selling_price
                            )
                        }}</span>
                    </div>
                </div>

                <div v-if="godmode">
                    <div class="flex justify-between">
                        <span
                            >{{
                                $t("offerSampleOfferBasePrice.Net cost")
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(pvOffer.totalNetCosts())
                        }}</span>
                    </div>
                </div>

                <div
                    class="flex justify-between"
                    v-if="godmode && pvOffer.$data.batterySelected"
                >
                    <span
                        >{{
                            $t("offerSampleOfferBasePrice.Costs total")
                        }}:</span
                    >
                    <span>{{
                        $numberHelper.formatMoney(
                            pvOffer.totalNetCosts() +
                                (pvOffer.$data.batterySelected
                                    ? pvOffer.$data.batterySelected.selling_price
                                    : 0)
                        )
                    }}</span>
                </div>

                <div
                    class="flex justify-between"
                    v-if="pvOffer.$data.forceCommissionVisibility || godmode"
                >
                    <span
                        >{{
                            $t(
                                "offerSampleOfferBasePrice.Minimum selling price"
                            )
                        }}:</span
                    >
                    <span>{{
                        $numberHelper.formatMoney(minimumSellingPriceNet)
                    }}</span>
                </div>

                <div class="flex justify-between" v-if="godmode">
                    <span
                        >{{
                            $t("offerSampleOfferBasePrice.Minimum margin")
                        }}:</span
                    >
                    <span>{{
                        $numberHelper.formatMoney(
                            minimumSellingPriceNet - pvOffer.totalNetCosts()
                        )
                    }}</span>
                </div>
                <div class="flex justify-between">
                    <span
                        >{{
                            $t("offerSampleOfferBasePrice.Starting price")
                        }}:</span
                    >
                    <span>{{
                        $numberHelper.formatMoney(baseSellingPriceNet)
                    }}</span>
                </div>
                <div class="flex justify-between">
                    <span
                        >{{
                            $t(
                                "offerSampleOfferBasePrice.Net price with a discount"
                            )
                        }}:</span
                    >
                    <span>{{ $numberHelper.formatMoneyWithDecimals(totalPriceNet) }}</span>
                </div>
                <div class="flex justify-between">
                    <span
                        >{{
                            $t(
                                "offerSampleOfferBasePrice.Net price with a discount"
                            )
                        }}/wp:</span
                    >
                    <span>{{
                        (totalPriceNet / 1000 / installationSize).toFixed(3)
                    }}</span>
                </div>

                <div class="flex justify-between font-bold">
                    <span
                        >{{
                            $t("offerSampleOfferBasePrice.Gross price")
                        }}:</span
                    >
                    <span>{{
                        $numberHelper.formatMoneyWithDecimals(totalPriceWithMarginWithVAT)
                    }}</span>
                </div>

                <div class="flex justify-between" v-if="subsidy.exists">
                    <span
                        >{{ subsidy.title }}:</span
                    >
                    <span>{{
                        $numberHelper.formatMoneyWithDecimals( subsidy.amount )
                    }}</span>
                </div>

                <div class="flex justify-between" v-if="financingModel && financingModel.isFinancingAvailable && financing.available">
                    <span
                        >{{ financing.title }} ( {{ financing.months  }} months ):</span
                    >
                    <span>{{
                        $numberHelper.formatMoneyWithDecimals( financing.monthlyPayment )
                    }}</span>
                </div>

                <div class="wrap">
                    <div v-if="discountAndPremiumFieldsVisible"
                         class="d-flex justify-space-between align-center my-1"
                    >
                        <span class="pt-3">{{ $t('offerSampleOfferBasePrice.Premium') }}:</span>

                        <input
                            type="number"
                            v-model="pvOffer.$data.userExtraPremium"
                            min="0"
                            :max="maxPremiumPV"
                            pattern="[0-9]"
                            class="border rounded-md px-2 py-1 w-[80px]"
                            :disabled="readMode"
                        />
                    </div>
                    <div
                        class="align-center text-red"
                        v-if="premiumError"
                    >
                        <span class="pt-3">{{ $t('offerSampleOfferBasePrice.Premium exceeds allowable limit') }}: {{ $numberHelper.formatMoneyWithDecimals( maxPremiumPV ) }}</span>
                    </div>
                    <div
                        v-if="discountAndPremiumFieldsVisible"
                        class="d-flex justify-space-between align-center my-1"
                    >
                        <span class="pt-3">
                            {{ $t("offerSampleOfferBasePrice.Discount") }}
                        </span>

                        <input
                            type="number"
                            v-model="pvOffer.$data.discount"
                            min="0"
                            :max="maxDiscount"
                            pattern="[0-9]"
                            class="border rounded-md px-2 py-1 w-[80px]"
                        />
                    </div>
                    <div
                        class="align-center text-red"
                        v-if="discountError"
                    >
                        <span class="pt-3">{{ $t('offerSampleOfferBasePrice.discount_error') }}</span>
                    </div>

                    <div
                        class="flex justify-between"
                        v-if="pvOffer.$data.forceCommissionVisibility || godmode"
                    >
                        <span
                            >{{
                                $t(
                                    "offerSampleOfferBasePrice.Merchant commission"
                                )
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(newSalesCommission)
                        }}</span>
                    </div>

                    <div class="flex justify-between" v-if="godmode">
                        <span
                            >{{
                                $t("offerSampleOfferBasePrice.company margin")
                            }}:</span
                        >
                        <span>{{
                            $numberHelper.formatMoney(newCompanyProfit)
                        }}</span>
                    </div>
                </div>

                <div v-if="!readMode && !playMode" class="flex gap-1">
                    <v-btn
                        variant="flat"
                        size="small"
                        color="primary"
                        @click="saveOffer"
                    >
                        {{ $t("offerSampleOfferBasePrice.Save") }}
                    </v-btn>

                    <input
                        class="mt-4"
                        type="checkbox"
                        v-model="discountAndPremiumFieldsVisible"
                        :checked="discountAndPremiumFieldsVisible"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BreezeCheckbox from "@/Components/Checkbox";
import BreezeInput from "@/Components/Input";
import BreezeLabel from "@/Components/Label";
import PvOfferModel from "@/models/PvOfferModel";
import PvRawOfferModel from "@/models/PvRawOfferModel";

export default {
    name: "SamplePvOffer",
    props: {
        offerBasis: {
            type: Object,
            required: true,
        },
        allSolarPanels: {
            type: Array,
            required: true,
        },
        allInverters: {
            type: Array,
            required: true,
        },
        allBatteries: {
            type: Array,
            required: true,
        },
        defaultInverterId: {
            type: Number,
            required: true,
        },
        defaultSolarPanelId: {
            type: Number,
            required: true,
        },
        solarPanelsNumber: {
            type: Number,
            required: false,
            default: 20,
        },
        roofSheathings: {
            type: Array,
            required: true,
        },
        pricingFactors: {
            type: Array,
            required: true,
        },
        minimumCompanyMargins: {
            type: Array,
            required: true,
        },
        baseMargins: {
            type: Array,
            required: true,
        },
        offer: {
            type: Object,
            required: false,
        },
        samplePvOfferIndex: {
            type: Number,
            required: false,
            default: 0,
        },
        readMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        playMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        locale: {
            type: String,
            required: true,
        },
        authUser: {
            type: Object,
            required: true,
        },
        annualConsumption: {
            type: Number,
            required: false,
            default: 5000,
        },
        extraCosts: {
            type: Array,
            required: false,
            default: () => []
        },
        subsidyModel: {
            type: Object,
            required: false,
            default: () => null
        },
        financingModel: {
            type: Object,
            required: false,
            default: () => null
        },
    },
    components: {
        BreezeCheckbox,
        BreezeInput,
        BreezeLabel,
    },
    setup(props) {
        const godmode = props.authUser.role !== "sales"

        const maxDiscountPv = parseInt(props.authUser.max_discount_pv)
        const maxPremiumPV = parseInt(props.authUser.max_premium_pv)
        let userExtraPremium = parseInt(props.authUser.extra_premium_pv)

        const solarPanelOptions =  props.allSolarPanels.map((o) => {
            return {
                title:
                    o.sales_points > 1
                        ? `${o.brand} ${o.model} [${o.sales_points}]`
                        : `${o.brand} ${o.model}`,
                value: o,
            }
        })

        let pvOffer;

        if (props.readMode) {
            pvOffer = new PvRawOfferModel({
                offer: props.offer,
                roofSheathings: props.roofSheathings,
                priceFactors: props.pricingFactors,
                userExtraPremium: userExtraPremium,
                offerBasis: props.offerBasis,
                samplePvOfferIndex: props.samplePvOfferIndex,
                allBatteries: props.allBatteries,
                allSolarPanels: props.allSolarPanels,
                allInverters: props.allInverters,
                extraCosts: props.extraCosts
            })
        } else {
            pvOffer = new PvOfferModel(
                {
                    defaultSolarPanelId: props.defaultSolarPanelId,
                    defaultInverterId: props.defaultInverterId,
                    roofSheathings: props.roofSheathings,
                    priceFactors: props.pricingFactors,
                    userExtraPremium: userExtraPremium,
                    offerBasis: props.offerBasis,
                    samplePvOfferIndex: props.samplePvOfferIndex,
                    allBatteries: props.allBatteries,
                    allSolarPanels: props.allSolarPanels,
                    allInverters: props.allInverters,
                    minimumCompanyMargins: props.minimumCompanyMargins,
                    baseMargins: props.baseMargins,
                    annualConsumption: props.annualConsumption,
                    dynamicConsumptionMode: false,
                    subsidy: 'none',
                    enablePowerOptimizers: true,
                    powerOptimizersAuto: false,
                    extraCosts: props.extraCosts,
                    subsidyModel: props.subsidyModel,
                    financingModel: props.financingModel,
                }
            )

        }

        return { pvOffer, godmode, solarPanelOptions, maxDiscountPv, maxPremiumPV }
    },
    data() {
        return {
            discountAndPremiumFieldsVisible: this.readMode && parseInt(this.offer.discount) > 0
        }
    },
    mounted() {
        this.pvOffer.changeInverter(this.suitableInverterOptions[0].value);
    },
    computed: {
        premiumError() {
            return this.maxPremiumPV !== null && this.pvOffer.$data.userExtraPremium > this.maxPremiumPV
        },
        discountError() {
            return this.pvOffer.$data.discount < 0 || this.pvOffer.$data.discount > this.maxDiscount || this.pvOffer.$data.discount === ''
        },
        maxDiscount() {
            let maxDiscount = this.baseSellingPriceNet - this.minimumSellingPriceNet
            if (this.maxDiscountPv !== null && maxDiscount > this.maxDiscountPv) {
                maxDiscount = this.maxDiscountPv
            }

            return maxDiscount
        },
        batteryOptions() {
            return this.pvOffer.batteriesCompatible()
                ? this.pvOffer.batteriesCompatible().map((o) => {
                    return {
                        title:
                            o.sales_points > 1
                                ? `${o.brand} ${o.model} [${o.sales_points}]`
                                : `${o.brand} ${o.model}`,
                        value: o,
                    };
                })
                : [];
        },
        suitableInverterOptions() {
            const suitableInverters = this.pvOffer.suitableInverters();
            const inverterSelected = this.pvOffer.$data.inverterSelected;

            return suitableInverters.length
                ? suitableInverters.map(inverter => ({
                    title: `${inverter.brand} ${inverter.model} (${this.$t("offerSampleOfferBasePrice.Phases")}: ${inverter.phases})${inverter.sales_points > 1 ? ` [${inverter.sales_points}]` : ''}`,
                    value: inverter,
                }))
                : [{
                    title: `${inverterSelected.brand} ${inverterSelected.model} (${this.$t("offerSampleOfferBasePrice.Phases")}: ${inverterSelected.phases})${inverterSelected.sales_points > 1 ? ` [${inverterSelected.sales_points}]` : ''}`,
                    value: inverterSelected,
                }];
        },
        minimumSellingPriceNet() {
            return this.pvOffer.minimumSellingPriceNet()
        },
        newSalesCommission() {
            return this.pvOffer.newSalesCommission()
        },
        newCompanyProfit() {
            return this.pvOffer.newCompanyProfit()
        },
        totalPriceNet() {
            return this.pvOffer.totalPriceNet()
        },
        baseSellingPriceNet() {
            return this.pvOffer.baseSellingPriceNet()
        },
        installationSize() {
            return this.pvOffer.installationSize()
        },
        areOptimizersAvailable() {
            return this.pvOffer.areOptimizersAvailable()
        },
        prices() {
            return this.pvOffer.prices()
        },
        totalPriceWithMarginWithVAT() {
            return this.pvOffer.totalPriceWithMarginWithVAT()
        },
        subsidy() {
            return this.pvOffer.getSubsidy()
        },
        financing() {
            return this.pvOffer.getFinancing()
        },
    },
    methods: {
        saveOffer() {
            if (this.pvOffer.$data.discount < 0) {
                return;
            }

            if (
                this.pvOffer.$data.discount > this.baseSellingPriceNet - this.minimumSellingPriceNet
            ) {
              console.log("Discount is too big")
                return;
            }

            if (this.discountError || this.premiumError) {
                return
            }

            this.$inertia.post("/offers", this.pvOffer.finalOfferData());
        },
        changeSolarPanel(item) {
            this.pvOffer.changeSolarPanel(item.value)
            this.$refs.selectedSolarPanelField.menu = false;
        },
        changeInverter(item) {
            this.$refs.selectedInverterField.menu = false;

            this.pvOffer.changeInverter(item.value)
        },
        changeBattery(item) {
            this.pvOffer.changeBattery(item.value)
            this.$refs.selectedBatteryField.menu = false;
        },
    },
};
</script>
